<script lang="ts">
  import { onMount } from "svelte";
  import AuthLayout from "./AuthLayout.svelte";
  import Field from "$lib/components/Field.svelte";
  import Button from "$lib/components/Button.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import { discoverSso, startEmailVerification, verifyEmailCode } from "$lib/api/endpoints";
  import type { SsoDiscovery } from "$lib/api/contract";
  import { AppError } from "$lib/api/problem";
  import { router } from "../../router.svelte";
  import { safeReturnPath, safeReturnTo } from "$lib/auth/returnTo";
  import { formatVerificationCode, isCompleteVerificationCode } from "$lib/auth/verificationCode";
  import { session } from "$lib/stores/session.svelte";
  import { track } from "$lib/telemetry/emit";
  import DevPasswordLogin from "./DevPasswordLogin.svelte";
  import { getAuthMethods } from "$lib/api/endpoints";

  let devPasswordLogin = $state(false);
  onMount(() => {
    let active = true;
    void getAuthMethods().then((methods) => {
      if (active) devPasswordLogin = methods.devPasswordLogin;
    }).catch(() => { /* Older or unavailable APIs retain email-only login. */ });
    return () => { active = false; };
  });

  let step = $state<"email" | "sent" | "saml">("email");
  let email = $state("");
  let discovery = $state<SsoDiscovery | null>(null);
  let error = $state<AppError | null>(null);
  let busy = $state(false);
  let requestId = $state("");
  let code = $state("");

  const returnTo = $derived(safeReturnTo(router.match.query));

  async function requestEmailLink(context: "login" | "saml_fallback"): Promise<boolean> {
    try {
      const started = await startEmailVerification({
        email: email.trim(),
        ...(returnTo !== "/" ? { returnTo } : {})
      });
      requestId = started.requestId;
      track({ name: "auth.email.requested", context });
      return true;
    } catch (err) {
      if (err instanceof AppError) error = err;
      return false;
    }
  }

  async function onContinue(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!email.trim() || busy) return;
    busy = true;
    error = null;
    try {
      const result = await discoverSso(email.trim());
      discovery = result;
      track({ name: "auth.sso.discover", method: result.method, localFallback: result.localFallback });
      if (result.method === "saml") {
        step = "saml";
      } else if (await requestEmailLink("login")) {
        step = "sent";
      }
    } catch (err) {
      if (err instanceof AppError) error = err;
    } finally {
      busy = false;
    }
  }

  async function onEmailFallback(): Promise<void> {
    if (busy) return;
    busy = true;
    error = null;
    if (await requestEmailLink("saml_fallback")) step = "sent";
    busy = false;
  }

  async function onResend(): Promise<void> {
    if (busy) return;
    busy = true;
    error = null;
    await requestEmailLink("login");
    busy = false;
  }

  async function onCodeSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!isCompleteVerificationCode(code) || !requestId || busy) return;
    busy = true;
    error = null;
    try {
      const auth = await verifyEmailCode(code, requestId);
      track({ name: "auth.email.verified", outcome: "success" });
      await session.refresh();
      router.navigate(safeReturnPath(auth.returnTo ?? returnTo), { replace: true });
    } catch (err) {
      track({ name: "auth.email.verified", outcome: "failure" });
      if (err instanceof AppError) error = err;
    } finally {
      busy = false;
    }
  }

  function onSaml(): void {
    if (!discovery?.startUrl) return;
    // Preserve the intended destination through the IdP round-trip.
    const url = new URL(discovery.startUrl, window.location.origin);
    if (returnTo !== "/" && !url.searchParams.has("returnTo")) url.searchParams.set("returnTo", returnTo);
    // Full navigation so the browser follows the IdP redirect chain.
    window.location.assign(url.toString());
  }

  function changeEmail(): void {
    step = "email";
    discovery = null;
    error = null;
    requestId = "";
    code = "";
  }
</script>

<AuthLayout
  title={step === "sent" ? "Check your email" : "Sign in or create your account"}
  subtitle={step === "sent" ? undefined : "Access your EpiDev organization portal."}
>
  {#if error}
    <Banner tone="error" title="We couldn't continue" correlationId={error.correlationId}>{error.userMessage}</Banner>
  {/if}

  {#if step === "email"}
    <form onsubmit={onContinue} class="stack">
      <Field
        id="login-email"
        label="Work email"
        type="email"
        bind:value={email}
        autocomplete="email"
        inputmode="email"
        placeholder="you@company.com"
        required
      />
      <Button type="submit" fullWidth loading={busy}>Continue with email</Button>
      <p class="flow-note">
        We'll email you a secure sign-in link — no password to remember. If you're new,
        your account is created automatically after you verify your email.
      </p>
    </form>
    {#if devPasswordLogin}
      <DevPasswordLogin bind:email {returnTo} />
    {/if}
  {:else if step === "sent"}
    <div class="stack" role="status" aria-live="polite">
      <p class="sent-copy">
        We sent a secure sign-in link to <span class="mono">{email}</span>. Open it, or
        choose <strong>Login via Code instead</strong> in the email.
      </p>
      <form onsubmit={onCodeSubmit} class="stack code-form">
        <Field
          id="login-code"
          label="One-time login code"
          bind:value={code}
          autocomplete="one-time-code"
          inputmode="text"
          placeholder="ABCD-EFGH-JKMN"
          mono
          required
          oninput={(value) => (code = formatVerificationCode(value))}
        />
        <Button type="submit" fullWidth loading={busy} disabled={!isCompleteVerificationCode(code)}>
          Continue with code
        </Button>
      </form>
      <p class="flow-note">
        The link expires shortly and can be used once. New accounts are created
        automatically after email verification — there are no passwords.
      </p>
      <Button variant="ghost" fullWidth loading={busy} onclick={onResend}>Resend email</Button>
      <button type="button" class="linkish center" onclick={changeEmail}>Use a different email</button>
    </div>
  {:else}
    <div class="stack">
      <div class="identity">
        <span class="mono">{email}</span>
        <button type="button" class="linkish" onclick={changeEmail}>Change</button>
      </div>
      <Button fullWidth onclick={onSaml}>Continue with your identity provider</Button>

      {#if discovery?.localFallback === "restricted"}
        <p class="fallback-note">
          Your organization requires sign-in through its identity provider. Contact an
          owner if you can't use it.
        </p>
      {:else}
        <button type="button" class="linkish center" onclick={onEmailFallback} disabled={busy}>
          Email me a sign-in link instead
        </button>
      {/if}
    </div>
  {/if}

  {#snippet footer()}
    Have an invitation or enterprise claim link? Open it to get started.
  {/snippet}
</AuthLayout>

<style>
  .identity {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    background: var(--bg-2);
    border: 1px solid var(--line-soft);
    border-radius: var(--radius-btn);
    font-size: 14px;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent);
    cursor: pointer;
    font-size: 13.5px;
    padding: 4px;
  }
  .linkish:hover {
    color: var(--accent-bright);
  }
  .linkish:disabled {
    color: var(--muted);
    cursor: default;
  }
  .center {
    align-self: center;
  }
  .fallback-note,
  .flow-note {
    font-size: 13px;
    color: var(--muted);
    margin: 0;
  }
  .sent-copy {
    font-size: 14.5px;
    margin: 0;
  }
  .code-form {
    padding-top: 4px;
  }
</style>
